"use client";

import React from "react";
import { motion, Variants } from "framer-motion";
import { Info, Target, Cpu, BookOpen, Compass, Award, ExternalLink, HelpCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";

export function AboutContent() {
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 80,
        damping: 15,
      },
    },
  };

  const features = [
    {
      icon: Compass,
      title: "Historical Explorer",
      description: "Instantly browse through multiple years of official CCMT cutoff data with high-performance filters for rounds, category, and paper code.",
    },
    {
      icon: Cpu,
      title: "Predictor Engine",
      description: "Our high-fidelity algorithm estimates your admission probability based on past trends, helping you design an optimized choice-filling strategy.",
    },
    {
      icon: Award,
      title: "NIRF Integration",
      description: "View National Institutional Ranking Framework (NIRF) rankings of universities right alongside cutoffs to evaluate college quality.",
    },
    {
      icon: BookOpen,
      title: "100% Free, No Paywalls",
      description: "No registration, no paywalls, no premium locks. Built by students to be completely free and accessible to every GATE aspirant across the nation.",
    },
  ];

  return (
    <div className="min-h-screen bg-background selection:bg-primary/30 pt-10 pb-20 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full -z-10 pointer-events-none">
        <div className="absolute top-[-5%] left-[-15%] w-[60%] h-[40%] bg-primary/10 blur-[130px] rounded-full opacity-60" />
        <div className="absolute bottom-[20%] right-[-15%] w-[60%] h-[40%] bg-secondary/10 blur-[130px] rounded-full opacity-60" />
      </div>

      <div className="container mx-auto px-6 max-w-5xl">
        {/* Header Hero Section */}
        <section className="text-center py-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface-container/50 border border-outline-variant/10 text-primary text-sm font-bold mb-6 shadow-sm backdrop-blur-md"
          >
            <Info className="size-4" />
            <span>Our Journey</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-3xl md:text-5xl font-display font-black tracking-tight text-foreground mb-6 leading-tight"
          >
            About CCMT College Finder
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-base md:text-lg text-on-surface-variant max-w-3xl mx-auto leading-relaxed"
          >
            Empowering GATE M.Tech aspirants with high-fidelity prediction tools, official cutoff insights, and a centralized portal to ease the counseling process. Built by engineers, for engineers.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-on-surface-variant bg-surface-container-low px-3 py-1 rounded-full border border-outline-variant/10 mt-4"
          >
            <span>Last Updated: June 12, 2026</span>
          </motion.div>
        </section>

        {/* Narrative & Mission */}
        <motion.section
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20"
        >
          <motion.div
            variants={itemVariants}
            className="p-8 rounded-3xl bg-surface-container border border-outline-variant/20 shadow-sm relative overflow-hidden flex flex-col justify-between"
          >
            <div className="absolute top-0 right-0 p-6 text-primary/10 pointer-events-none">
              <Target className="size-36 -mr-10 -mt-10" />
            </div>
            <div>
              <div className="flex items-center gap-3 text-primary mb-6">
                <Target className="size-6" />
                <h2 className="font-display font-bold text-xl md:text-2xl text-foreground">Our Mission</h2>
              </div>
              <p className="text-on-surface-variant text-sm md:text-base leading-relaxed mb-6">
                CCMT counseling is one of the most critical stages in an engineering student&apos;s career. However, parsing official PDFs containing thousands of cutoff records across multiple rounds, branches, and categories is a tedious and stressful task that takes hours of manual effort.
              </p>
              <p className="text-on-surface-variant text-sm md:text-base leading-relaxed">
                Our mission is to simplify this complexity. We want to put accurate, searchable, and filtered historical data at the fingertips of every aspirant — completely free, with no registration required. By doing so, we ensure that students can make informed decisions, write optimized choice lists, and secure admission into the best possible programs.
              </p>
            </div>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="p-8 rounded-3xl bg-surface-container border border-outline-variant/20 shadow-sm relative overflow-hidden flex flex-col justify-between"
          >
            <div className="absolute top-0 right-0 p-6 text-primary/10 pointer-events-none">
              <BookOpen className="size-36 -mr-10 -mt-10" />
            </div>
            <div>
              <div className="flex items-center gap-3 text-primary mb-6">
                <BookOpen className="size-6" />
                <h2 className="font-display font-bold text-xl md:text-2xl text-foreground">Why We Built This</h2>
              </div>
              <p className="text-on-surface-variant text-sm md:text-base leading-relaxed mb-6">
                <strong className="text-foreground">Piyush Prajapati</strong> and <strong className="text-foreground">Ankit Yadav</strong>, two final-year engineering students and developers from India, went through the CCMT counseling process firsthand. The existing tools were slow, cluttered with ads, or locked essential features behind paywalls. The official data was scattered across hundreds of PDF pages.
              </p>
              <p className="text-on-surface-variant text-sm md:text-base leading-relaxed">
                They built CCMT College Finder from the ground up using modern web technologies, meticulously parsing and verifying 6 years of official seat allotment data to create a fast, free, and genuinely useful alternative that respects students&apos; time and intelligence.
              </p>
            </div>
          </motion.div>
        </motion.section>

        {/* Feature Cards Grid */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="font-display font-black text-2xl md:text-3xl text-foreground mb-4">Core Platform Highlights</h2>
            <p className="text-on-surface-variant max-w-xl mx-auto text-sm md:text-base">
              Explore the advanced features that make CCMT College Finder the ultimate toolkit for counseling research.
            </p>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
          >
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="p-6 rounded-2xl bg-surface-container-low border border-outline-variant/20 hover:border-primary/30 transition-all duration-300 shadow-sm flex gap-4"
                >
                  <div className="flex-shrink-0 size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <Icon className="size-6" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-lg text-foreground mb-2">{feature.title}</h3>
                    <p className="text-on-surface-variant text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </section>

        {/* Feedback & Improvement Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-8 md:p-10 rounded-3xl bg-primary/5 border border-primary/20 text-center relative overflow-hidden shadow-sm"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-50 pointer-events-none" />
          <div className="relative z-10 space-y-6">
            <h2 className="text-xl md:text-3xl font-display font-black text-foreground">
              Help Us Improve
            </h2>
            <p className="text-on-surface-variant max-w-2xl mx-auto text-sm md:text-base">
              CCMT College Finder is designed to help students make informed decisions. If you have any suggestions, found a bug, or want to share feedback to make the predictor more accurate, we would love to hear from you.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link href="/contact">
                <Button className="h-12 px-8 rounded-xl font-bold gap-2">
                  <span>Get in Touch</span>
                  <ExternalLink className="size-4" />
                </Button>
              </Link>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
